// src/sections/gamification/WelcomeTour.jsx
// Gamification welcome tour: a one-time full-screen walkthrough shown on
// portal open once the brand flips missions on, so hosts learn the new
// program the moment it exists instead of discovering a mystery nav item.
// Mounted once in the authed dashboard layout AFTER CelebrationOverlay (later
// in the DOM = on top when both open; dismissing the tour reveals any queued
// celebration, which is the right order for a first run). Fully self-gating:
// no-op unless missions are enabled, and a per-user localStorage seen-marker
// (same device-local model as the celebration seen-sets) keeps it to one
// showing. Steps self-gate on config — a missions-only brand gets a shorter
// tour, the full stack gets five steps. Replayable via openWelcomeTour()
// (the Missions page keeps a "replay the tour" link at the bottom).

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

import { Hero, NIGHT, nightStage, Atmosphere, GoldCTA, nightTileSx } from './plays/uiKit';
import { particleBurst } from './plays/confetti';
import { money, perkLabel } from '../../utils/gamificationPerks';
import { useAuth } from '../../lib/auth';
import { EventContext } from '../../event/eventContext';
import useGamificationConfig from '../../hooks/useGamificationConfig';

const TOUR_EVENT = 'astra:welcomeTourOpen';
const seenKey = (userDocId) => `welcomeTourSeen_v1_${userDocId}`;

// Re-open the tour from anywhere (Missions page replay link). Kept here so
// the event name lives in exactly one file.
export function openWelcomeTour() {
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

const goldChipSx = { bgcolor: 'rgba(245,198,68,0.16)', color: '#ffe9a3', fontWeight: 700, border: '1px solid rgba(245,198,68,0.45)' };

// The launchpad step's mission list: real titles + real dollar chips pulled
// from the brand's live config so the promise on screen is the one the
// engine actually pays. First track = the brand's starter track ("Launch
// Pad" at Club Astra; whatever another brand configures elsewhere). No track
// configured => neutral title over the loose catalog.
function launchpadFacts(missions) {
  const track = missions.tracks[0] || null;
  const trackIds = track && Array.isArray(track.missionIds) ? track.missionIds : [];
  const pool = trackIds.length ? missions.catalog.filter((m) => trackIds.includes(m.id)) : missions.catalog;
  const cashMissions = pool.filter((m) => m.rewardType === 'cash' && Number(m.rewardAmount) > 0);
  const finisher = Number(track?.finisherRewardCash) || 0;
  const totalCash = cashMissions.reduce((sum, m) => sum + Number(m.rewardAmount), 0) + finisher;
  return { trackTitle: track?.title || 'Missions that pay', trackSize: pool.length, cashMissions, finisher, totalCash };
}

// Perk examples for the closing step, from the brand's own config (mission
// perk rewards + XP perk shop) so the tour never promises a prize this brand
// doesn't run. Empty config => generic line.
function perkExamples(config) {
  const labels = [];
  const push = (perk) => {
    if (!perk || !perk.type) return;
    const label = perkLabel(perk.type, perk.label);
    if (label && !labels.some((l) => l.toLowerCase() === label.toLowerCase())) labels.push(label);
  };
  config.missions.catalog.forEach((m) => {
    if (m.rewardType === 'perk') push(m.perk);
  });
  (config.game.perkShop || []).forEach((item) => push(item.perk));
  return labels.slice(0, 3);
}

function buildSteps(config, brandName) {
  const { trackTitle, trackSize, cashMissions, finisher, totalCash } = launchpadFacts(config.missions);

  const steps = [
    {
      key: 'welcome',
      asset: 'champagne-bottle',
      glow: 'rgba(139,92,246,0.4)',
      kicker: 'SOMETHING NEW',
      title: 'THE GAME IS ON',
      body: `Hosting${brandName ? ` at ${brandName}` : ''} just got a lot more rewarding. Missions, perks and real cash, built on what you already do on your nights. Here’s the 60-second tour.`
    },
    {
      key: 'launchpad',
      asset: 'mission-crest',
      glow: 'rgba(245,198,68,0.35)',
      kicker: 'GET PAID',
      title: String(trackTitle).toUpperCase(),
      body: cashMissions.length
        ? 'Your starter missions. Complete them and the cash lands in your host wallet, the same wallet your pays live in.'
        : 'Your starter missions pay real cash into your host wallet. Check the Missions page for what’s live this season.',
      extra: cashMissions.length ? (
        <Stack spacing={1} sx={{ mt: 2, textAlign: 'left' }}>
          {cashMissions.slice(0, 3).map((m) => (
            <Box key={m.id} sx={{ ...nightTileSx, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 700, color: NIGHT.white, minWidth: 0 }}>
                {m.title || m.id}
              </Typography>
              <Chip size="small" label={money(m.rewardAmount)} sx={{ ...goldChipSx, flexShrink: 0 }} />
            </Box>
          ))}
          {finisher > 0 ? (
            <Box sx={{ ...nightTileSx, py: 1, borderColor: 'rgba(245,198,68,0.4)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 700, color: NIGHT.goldHi, minWidth: 0 }}>
                Finish all {trackSize} in one season
              </Typography>
              <Chip size="small" label={`${money(finisher)} bonus`} sx={{ ...goldChipSx, flexShrink: 0 }} />
            </Box>
          ) : null}
          {totalCash > 0 ? (
            <Typography sx={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: NIGHT.goldHi, letterSpacing: '0.08em' }}>
              UP TO {money(totalCash)} ON THE TABLE THIS SEASON
            </Typography>
          ) : null}
        </Stack>
      ) : null
    }
  ];

  if (config.game.enabled) {
    // Rank names come from the brand's own ladder, not a hardcoded
    // Bronze→Legend assumption; ladder mention self-gates on configured rungs.
    const levels = Array.isArray(config.game.levels) ? config.game.levels : [];
    const firstLevel = levels[0]?.name;
    const lastLevel = levels.length > 1 ? levels[levels.length - 1]?.name : null;
    const climb = firstLevel && lastLevel ? `Climb ${firstLevel} to ${lastLevel}` : 'Climb the ranks';
    const hasLadder = (config.game.seasonPass?.rungs || []).length > 0;
    steps.push({
      key: 'levels',
      asset: 'xp-token',
      glow: 'rgba(34,211,238,0.3)',
      kicker: 'LEVEL UP',
      title: 'XP, LEVELS & STREAKS',
      body: `Every payer, sign-in and win earns XP. ${climb} for bigger perks, and keep a weekly streak alive to multiply your XP.${
        hasLadder ? ' The season pass ladder shows exactly what unlocks next.' : ''
      }`
    });
  }

  // Games step lists only what this brand actually runs.
  const gameBits = [];
  if (config.game.enabled) {
    if (config.quests.enabled) gameBits.push('weekly quests');
    if (config.predictions.enabled) gameBits.push('night predictions');
    if (config.game.plays?.enabled) gameBits.push('arcade plays');
    if (config.wheel.enabled) gameBits.push('a prize wheel every time you level up');
  }
  if (gameBits.length) {
    const list = gameBits.length === 1 ? gameBits[0] : `${gameBits.slice(0, -1).join(', ')}, plus ${gameBits[gameBits.length - 1]}`;
    steps.push({
      key: 'games',
      asset: 'mystery-crate',
      glow: 'rgba(139,92,246,0.4)',
      kicker: 'PLAY',
      title: 'GAMES & PRIZES',
      body: `${list.charAt(0).toUpperCase()}${list.slice(1)}. Quick hits that pay XP, perks and sometimes cash.`
    });
  }

  const perks = perkExamples(config);
  steps.push({
    key: 'perks',
    asset: 'drink-card',
    glow: 'rgba(245,198,68,0.35)',
    kicker: 'SPEND IT',
    title: 'PERKS AT THE DOOR',
    body: `Prizes drop into your Perk Wallet as vouchers: ${
      // labels keep their config casing: brands name perks after proper nouns
      perks.length ? `${perks.join(', ')} and more` : 'real prizes you redeem on a night out'
    }. Show your phone at the door and staff redeem it on the spot.`
  });

  return steps;
}

// Confetti send-off on the final step, in-palette (gold + white).
function FinaleBurst() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const stop = particleBurst(ref.current, {
      x: ref.current.clientWidth / 2,
      y: ref.current.clientHeight * 0.28,
      count: 110,
      power: 8
    });
    return stop;
  }, []);
  return (
    <Box
      component="canvas"
      ref={ref}
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden
    />
  );
}

export default function WelcomeTour() {
  const { profile } = useAuth() || {};
  const eventCtx = useContext(EventContext);
  const { config, loading: configLoading } = useGamificationConfig();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const userDocId = profile?.id || null;
  const brandName = eventCtx?.brand?.name || '';
  const enabled = !configLoading && config.missions.enabled && Boolean(userDocId);
  const steps = useMemo(() => (enabled ? buildSteps(config, brandName) : []), [enabled, config, brandName]);

  // First-run auto-open, one showing per user per device.
  useEffect(() => {
    if (!enabled) return;
    try {
      if (!localStorage.getItem(seenKey(userDocId))) {
        setStep(0);
        setOpen(true);
      }
    } catch {
      /* storage unavailable — skip rather than loop forever */
    }
  }, [enabled, userDocId]);

  // Manual replay from the Missions page.
  useEffect(() => {
    if (!enabled) return undefined;
    const onOpen = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(TOUR_EVENT, onOpen);
    return () => window.removeEventListener(TOUR_EVENT, onOpen);
  }, [enabled]);

  if (!open || !steps.length) return null;

  const markSeen = () => {
    try {
      localStorage.setItem(seenKey(userDocId), new Date().toISOString());
    } catch {
      /* fine — it just shows again next visit */
    }
  };
  const dismiss = (thenNavigate) => {
    markSeen();
    setOpen(false);
    if (thenNavigate) navigate('/missions');
  };

  const s = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;

  return (
    <Dialog
      open
      onClose={() => dismiss(false)}
      maxWidth="xs"
      fullWidth
      // Above the default MuiDialog z-index (1300): the celebration/wheel
      // takeover and legacy portal-open dialogs mount after the tour (their
      // Firestore loads finish after our localStorage check) and would
      // otherwise stack on top. First run reads tour → celebrations → rest.
      sx={{ zIndex: 1400 }}
      // On short phones the tallest step (the launchpad list) used to be
      // clipped by overflow:hidden against the default height cap. Give mobile
      // a little more room (tighter margins + taller cap) and let it scroll
      // inside the card as a last resort instead of cutting the buttons off.
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflowX: 'hidden',
          overflowY: 'auto',
          my: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          background: nightStage,
          color: NIGHT.white
        }
      }}
    >
      <Box sx={{ position: 'relative', px: 3, pt: 2, pb: 3, textAlign: 'center' }}>
        <Atmosphere />
        {isLast ? <FinaleBurst /> : null}
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 470 }}>
          {/* top row: step count + skip */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: NIGHT.dim }}>
              {step + 1} / {steps.length}
            </Typography>
            <Button size="small" onClick={() => dismiss(false)} sx={{ color: 'rgba(245,243,255,0.6)', fontWeight: 600 }}>
              Skip
            </Button>
          </Stack>

          {/* per-step content, re-keyed so each step slides in */}
          <Box
            key={s.key}
            sx={{
              flexGrow: 1,
              animation: 'tourStepIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both',
              '@keyframes tourStepIn': {
                from: { transform: 'translateX(26px)', opacity: 0 },
                to: { transform: 'none', opacity: 1 }
              }
            }}
          >
            {/* spotlight pool behind the hero */}
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: -26,
                  width: 260,
                  height: 260,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${s.glow.replace(/0\.\d+\)$/, '0.2)')} 0%, transparent 62%)`,
                  filter: 'blur(6px)',
                  pointerEvents: 'none'
                }}
              />
              <Hero asset={s.asset} size={s.extra ? 96 : 160} glow={s.glow} />
            </Box>
            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: '0.42em',
                textIndent: '0.42em',
                color: NIGHT.violetSoft,
                textShadow: '0 0 16px rgba(139,92,246,0.6)'
              }}
            >
              {s.kicker}
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontWeight: 900,
                fontSize: { xs: 26, sm: 30 },
                letterSpacing: '0.06em',
                lineHeight: 1.12,
                color: NIGHT.white,
                textShadow: '0 0 26px rgba(167,139,250,0.45), 0 2px 14px rgba(0,0,0,0.8)'
              }}
            >
              {s.title}
            </Typography>
            <Typography sx={{ mt: 1, px: 0.5, fontSize: 14, lineHeight: 1.6, color: NIGHT.dim }}>{s.body}</Typography>
            {s.extra || null}
          </Box>

          {/* dots */}
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2.5, mb: 2 }}>
            {steps.map((st, i) => (
              <Box
                key={st.key}
                sx={{
                  width: i === step ? 22 : 8,
                  height: 8,
                  borderRadius: 99,
                  transition: 'all 0.25s ease',
                  background: i === step ? `linear-gradient(90deg, ${NIGHT.violet}, ${NIGHT.cyan})` : 'rgba(167,139,250,0.3)',
                  boxShadow: i === step ? '0 0 10px rgba(34,211,238,0.5)' : 'none'
                }}
              />
            ))}
          </Stack>

          {/* controls */}
          {isLast ? (
            <Stack spacing={1} alignItems="center">
              <GoldCTA onClick={() => dismiss(true)} sx={{ px: 4, fontSize: 14 }}>
                START MY FIRST MISSION
              </GoldCTA>
              {/* Host Playbook cross-link: the tour sells the game; the
                  playbook teaches the job. Quiet tertiary action. */}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  dismiss(false);
                  navigate('/playbook');
                }}
                sx={{ color: NIGHT.violetSoft, fontWeight: 700, fontSize: 12, letterSpacing: '0.08em' }}
              >
                BROWSE THE FULL PLAYBOOK
              </Button>
              <Button variant="text" onClick={() => dismiss(false)} sx={{ color: 'rgba(245,243,255,0.6)' }}>
                Close
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Button
                variant="text"
                onClick={() => setStep((v) => Math.max(0, v - 1))}
                disabled={step === 0}
                sx={{ color: 'rgba(245,243,255,0.6)', visibility: step === 0 ? 'hidden' : 'visible' }}
              >
                Back
              </Button>
              <ButtonBase
                onClick={() => setStep((v) => Math.min(steps.length - 1, v + 1))}
                sx={{
                  height: 42,
                  px: 4,
                  borderRadius: 99,
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textIndent: '0.12em',
                  fontFamily: 'inherit',
                  color: '#fff',
                  background: 'linear-gradient(180deg, #8b5cf6 0%, #6d3ff2 100%)',
                  boxShadow: '0 0 18px rgba(139,92,246,0.45), 0 6px 16px rgba(0,0,0,0.45)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 0 26px rgba(139,92,246,0.6), 0 8px 20px rgba(0,0,0,0.45)' }
                }}
              >
                NEXT
              </ButtonBase>
            </Stack>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
